import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SOCIAL_OTHER_THEME_ID,
  SocialTopicCountCell,
  SocialTopicCountsResponse,
  SocialTopicMetric,
  SocialTopicPostItem,
} from "@digital-twins/shared-types";
import { cn } from "@/lib/utils";
import { measureOf } from "./charts/socialTopicMetrics";
import { SocialBoardRail, SocialBoardRailItem } from "./SocialBoardRail";
import { formatSocialMeasure } from "./SocialPostTile";
import { SocialThemeSection } from "./SocialThemeSection";
import { SocialPlayingPostProvider } from "./SocialPlayingPostContext";
import { socialHeadingText } from "./socialFormat";
import { SocialTopicSlotContext } from "./SocialTopicView";
import { registerTopicHotkey } from "./useTopicHotkeys";

interface SocialBoardProps {
  context: SocialTopicSlotContext;
  /** A theme id or SOCIAL_OTHER_THEME_ID. */
  themeId: string;
  title: string;
  /** The theme's count cell under the filters (posts and views for the header). */
  cell: SocialTopicCountCell;
  /** Posts under the filters, all themes: the "% of topic" denominator. */
  topicTotal: number;
  /** Expanded fills the stage with 5 masonry columns; the switch is instant. */
  isExpanded: boolean;
  /** Absent hides the expand button (a lone board already fills the stage). */
  onToggleExpand?: () => void;
  /** Tile quick action "Ask about this post"; absent hides it. */
  onAskPost?: (post: SocialTopicPostItem) => void;
  /** Tile quick action "Hide post"; absent hides it. */
  onHidePost?: (post: SocialTopicPostItem) => void;
  /** The board's loaded posts each time a page lands (for `j` / `k`). */
  onPostsChange?: (themeId: string, posts: Array<SocialTopicPostItem>) => void;
  /** Far from view in the row: the board sends no request yet. */
  isDeferred?: boolean;
  /** The only board is Other: its name and "100% of topic" say nothing, so the feed shows bare. */
  isHeaderless?: boolean;
}

/** Boards that load at once; the rest load as they near view (one board width away). */
export const SOCIAL_EAGER_BOARDS = 2;

/** A board of the row: one theme and its count cell. */
export interface SocialBoardEntry {
  /** A theme id or SOCIAL_OTHER_THEME_ID. */
  id: string;
  title: string;
  cell: SocialTopicCountCell;
}

/**
 * Boards in the current measure, largest first (reach: summed views; posts:
 * the count), then posts, then the Topic's own theme order. A new array.
 */
export function orderSocialBoards(
  boards: Array<SocialBoardEntry>,
  metric: SocialTopicMetric,
): Array<SocialBoardEntry> {
  return boards
    .map((board, index) => ({ board, index }))
    .sort(
      (a, b) =>
        measureOf(metric, b.board.cell.count, b.board.cell.views) -
          measureOf(metric, a.board.cell.count, a.board.cell.views) ||
        b.board.cell.count - a.board.cell.count ||
        a.index - b.index,
    )
    .map(({ board }) => board);
}

/**
 * The post `direction` steps from `postToken` in the boards' reading order
 * (wrapping), or null when the list is empty. A token no longer listed starts
 * from the first post.
 */
export function stepSocialBoardPost(
  posts: Array<SocialTopicPostItem>,
  postToken: string | null,
  direction: 1 | -1,
): SocialTopicPostItem | null {
  if (posts.length === 0) {
    return null;
  }
  const at = posts.findIndex((post) => post.token === postToken);
  if (at < 0) {
    return posts[0];
  }
  return posts[(at + direction + posts.length) % posts.length];
}

/** The board's figure in the measure, formatted (the rail chips). */
function boardValueLabel(
  cell: SocialTopicCountCell,
  metric: SocialTopicMetric,
  uiLanguage: string,
): string {
  return formatSocialMeasure(measureOf(metric, cell.count, cell.views), uiLanguage);
}

/**
 * One theme board: header metrics, then its posts in an inner scroll with
 * masonry and paging, in the page's sort. The page's filters apply, except
 * the theme, which is the board's own.
 */
export function SocialBoard(props: SocialBoardProps) {
  const {
    context,
    themeId,
    title,
    cell,
    topicTotal,
    isExpanded,
    onToggleExpand,
    onAskPost,
    onHidePost,
    onPostsChange,
    isDeferred = false,
    isHeaderless = false,
  } = props;

  const { topicFilters } = context;

  return (
    <SocialThemeSection
      universeToken={context.universe.token}
      periodFrom={context.universe.period.from}
      themeId={themeId}
      title={title}
      count={cell.count}
      views={cell.views ?? 0}
      topicTotal={topicTotal}
      metric={topicFilters.filters.metric}
      filters={topicFilters.facetFilters("theme")}
      sort={context.sort}
      refreshKey={context.refreshKey}
      themeNames={context.themeNames}
      brandNames={context.brandNames}
      brandLogoPaths={context.brandLogoPaths}
      isExpanded={isExpanded}
      onSeeAll={onToggleExpand ? () => onToggleExpand() : undefined}
      onOpenPost={context.onOpenPost}
      onAskPost={onAskPost}
      onHidePost={onHidePost}
      topicName={context.universe.name}
      hiddenPostTokens={context.hiddenPostTokens}
      onPostsChange={onPostsChange}
      isDeferred={isDeferred}
      isHeaderless={isHeaderless}
      className={cn(
        "shrink-0 grow-0 snap-start",
        isExpanded
          ? "basis-full"
          : "basis-[88%] @[760px]:basis-[calc((100%-16px)/2)]",
      )}
    />
  );
}

interface SocialBoardsProps {
  context: SocialTopicSlotContext;
  /** Theme counts under every filter but the theme (`facetFilters("theme")`). */
  themeCounts: SocialTopicCountsResponse | null;
  onAskPost?: (post: SocialTopicPostItem) => void;
  onHidePost?: (post: SocialTopicPostItem) => void;
  /** Every loaded board post in reading order (board by board), for the sheet's `j` / `k`. */
  onPostsChange?: (posts: Array<SocialTopicPostItem>) => void;
  /** The post open in the sheet: the boards on either side of its board load, so `j` / `k` reach them. */
  openPostToken?: string | null;
}

/**
 * The boards (the hero): "Themes" with its subtitle, the rail and arrows,
 * then a scroll-snap row with 2 boards in view, ordered by the current
 * measure. A board expands into the whole stage instantly (D9); a theme
 * filter shows that board expanded. Esc collapses.
 */
export function SocialBoards(props: SocialBoardsProps) {
  const { context, themeCounts, onAskPost, onHidePost, onPostsChange, openPostToken = null } =
    props;

  const { t, i18n } = useTranslation("app");
  const { universe, topicFilters } = context;
  const { metric, themeId: filteredThemeId } = topicFilters.filters;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [postsByTheme, setPostsByTheme] = useState<
    Map<string, Array<SocialTopicPostItem>>
  >(() => new Map());
  // Boards that loaded once (eager, near view, expanded, or next to the open
  // post); they keep their posts from then on, wherever the order moves them.
  const [loadedIds, setLoadedIds] = useState<Set<string>>(() => new Set());
  const rowRef = useRef<HTMLDivElement | null>(null);
  const onPostsChangeRef = useRef(onPostsChange);
  onPostsChangeRef.current = onPostsChange;

  const boards = useMemo(() => {
    const cellOf = (id: string): SocialTopicCountCell =>
      themeCounts?.cells.find((cell) => cell.key === id) ?? {
        key: id,
        count: 0,
        small: true,
        views: 0,
        viewsPosts: 0,
      };
    const entries: Array<SocialBoardEntry> = universe.topics.map((topic) => ({
      id: topic.id,
      title: socialHeadingText(topic.name),
      cell: cellOf(topic.id),
    }));
    const other = cellOf(SOCIAL_OTHER_THEME_ID);
    if (other.count > 0) {
      entries.push({
        id: SOCIAL_OTHER_THEME_ID,
        title: t("socialListening.topicView.other"),
        cell: other,
      });
    }
    return orderSocialBoards(entries, metric);
  }, [universe.topics, themeCounts, metric, t]);

  // One theme: its board takes the full width at once, with no rail or expand.
  const isSingle = boards.length === 1;
  const soloId = filteredThemeId ?? expandedId ?? (isSingle ? boards[0].id : null);
  const visibleBoards = soloId
    ? boards.filter((board) => board.id === soloId)
    : boards;
  const focusId =
    soloId ??
    (activeId && boards.some((board) => board.id === activeId)
      ? activeId
      : (boards[0]?.id ?? null));
  const topicTotal = themeCounts?.total ?? 0;
  const orderKey = visibleBoards.map((board) => board.id).join("|");

  // `j` / `k` read the visible boards' posts in reading order.
  useEffect(() => {
    const ordered = orderKey
      .split("|")
      .filter(Boolean)
      .flatMap((id) => postsByTheme.get(id) ?? []);
    const seen = new Set<string>();
    onPostsChangeRef.current?.(
      ordered.filter((post) => {
        if (seen.has(post.token)) {
          return false;
        }
        seen.add(post.token);
        return true;
      }),
    );
  }, [orderKey, postsByTheme]);

  function markLoaded(ids: Array<string>) {
    setLoadedIds((previous) => {
      if (ids.every((id) => previous.has(id))) {
        return previous;
      }
      const next = new Set(previous);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }

  // The first two boards and an expanded one load at once, and stay loaded.
  useEffect(() => {
    const ids = orderKey.split("|").filter(Boolean).slice(0, SOCIAL_EAGER_BOARDS);
    markLoaded(soloId ? [...ids, soloId] : ids);
  }, [orderKey, soloId]);

  // An open post's neighbour boards load, so `j` / `k` step into them rather
  // than wrapping over boards that have not loaded yet.
  useEffect(() => {
    if (!openPostToken || soloId) {
      return;
    }
    const ids = orderKey.split("|").filter(Boolean);
    const at = ids.findIndex((id) =>
      (postsByTheme.get(id) ?? []).some((post) => post.token === openPostToken),
    );
    if (at < 0) {
      return;
    }
    markLoaded([ids[(at + 1) % ids.length], ids[(at - 1 + ids.length) % ids.length]]);
  }, [openPostToken, orderKey, postsByTheme, soloId]);

  // Lazy boards: past the first two, a board sends its requests only once it
  // comes within about one board width of the row's visible part.
  const canObserve = typeof IntersectionObserver !== "undefined";
  function isBoardDeferred(id: string, index: number): boolean {
    return (
      canObserve && soloId == null && index >= SOCIAL_EAGER_BOARDS && !loadedIds.has(id)
    );
  }
  const deferredKey = visibleBoards
    .filter((board, index) => isBoardDeferred(board.id, index))
    .map((board) => board.id)
    .join("|");
  useEffect(() => {
    const row = rowRef.current;
    if (!row || !canObserve || soloId) {
      return undefined;
    }
    const boardWidth = row.querySelector<HTMLElement>("[data-board]")?.offsetWidth ?? 0;
    const margin = boardWidth > 0 ? `${boardWidth}px` : "50%";
    const observer = new IntersectionObserver(
      (entries) => {
        const reached = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => (entry.target as HTMLElement).dataset.board)
          .filter((id): id is string => Boolean(id));
        if (reached.length > 0) {
          markLoaded(reached);
        }
      },
      { root: row, rootMargin: `0px ${margin} 0px ${margin}` },
    );
    row
      .querySelectorAll<HTMLElement>('[data-deferred="true"]')
      .forEach((board) => observer.observe(board));
    return () => observer.disconnect();
  }, [canObserve, orderKey, soloId, deferredKey]);

  useEffect(() => {
    return registerTopicHotkey(
      "Escape",
      () => {
        // An open sheet or menu takes Esc first.
        if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"]')) {
          return;
        }
        collapse();
      },
      { enabled: (filteredThemeId ?? expandedId) != null },
    );
    // `collapse` only reads the latest filters through the setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soloId, filteredThemeId]);

  function collapse() {
    setExpandedId(null);
    if (filteredThemeId) {
      topicFilters.setFilter("themeId", null);
    }
  }

  function toggleExpand(id: string) {
    if (soloId === id) {
      collapse();
      return;
    }
    setActiveId(id);
    setExpandedId(id);
  }

  function scrollToBoard(id: string) {
    const row = rowRef.current;
    const board = Array.from(
      row?.querySelectorAll<HTMLElement>("[data-board]") ?? [],
    ).find((element) => element.dataset.board === id);
    if (!row || !board || typeof row.scrollTo !== "function") {
      return;
    }
    const isReduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    row.scrollTo({
      left: board.offsetLeft - row.offsetLeft,
      behavior: isReduced ? "auto" : "smooth",
    });
  }

  function select(id: string) {
    setActiveId(id);
    if (filteredThemeId) {
      topicFilters.setFilter("themeId", id);
      return;
    }
    if (expandedId) {
      setExpandedId(id);
      return;
    }
    scrollToBoard(id);
  }

  function step(direction: 1 | -1) {
    if (boards.length === 0) {
      return;
    }
    const at = Math.max(
      0,
      boards.findIndex((board) => board.id === focusId),
    );
    select(boards[(at + direction + boards.length) % boards.length].id);
  }

  function onBoardPosts(themeId: string, posts: Array<SocialTopicPostItem>) {
    setPostsByTheme((previous) => {
      if (previous.get(themeId) === posts) {
        return previous;
      }
      const next = new Map(previous);
      next.set(themeId, posts);
      return next;
    });
  }

  const railItems: Array<SocialBoardRailItem> = boards.map((board) => ({
    id: board.id,
    label: board.title,
    valueLabel: boardValueLabel(board.cell, metric, i18n.language),
  }));

  return (
    // One player at a time across every board (plan D3).
    <SocialPlayingPostProvider>
    <section
      className="@container"
      aria-labelledby="social-boards-heading"
      data-testid="social-boards"
    >
      <div className="mb-3 mt-7 flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2
            id="social-boards-heading"
            className="font-display text-[30px] font-normal leading-[1.1] text-foreground"
          >
            {t("socialListening.topicPage.boards.title")}
          </h2>
          <span
            className="text-[13px] text-muted-foreground"
            data-testid="social-boards-subtitle"
          >
            {t(`socialListening.topicPage.boards.subtitle.${metric}`, {
              // The Topic's own themes: "Other" holds the posts outside them.
              count: boards.filter((board) => board.id !== SOCIAL_OTHER_THEME_ID).length,
            })}
          </span>
        </div>
        {isSingle ? null : (
          <SocialBoardRail
            items={railItems}
            activeId={focusId}
            onSelect={select}
            onPrevious={() => step(-1)}
            onNext={() => step(1)}
          />
        )}
      </div>
      <div
        ref={rowRef}
        className="flex scroll-mt-[72px] snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:thin]"
        data-testid="social-boards-row"
      >
        {visibleBoards.map((board, index) => (
          <SocialBoard
            key={board.id}
            context={context}
            themeId={board.id}
            title={board.title}
            cell={board.cell}
            topicTotal={topicTotal}
            isExpanded={soloId === board.id}
            onToggleExpand={isSingle ? undefined : () => toggleExpand(board.id)}
            onAskPost={onAskPost}
            onHidePost={onHidePost}
            onPostsChange={onBoardPosts}
            isDeferred={isBoardDeferred(board.id, index)}
            isHeaderless={isSingle && board.id === SOCIAL_OTHER_THEME_ID}
          />
        ))}
      </div>
    </section>
    </SocialPlayingPostProvider>
  );
}
